import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { MdAdd, MdDelete, MdEdit, MdMoreVert, MdSettings } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import InlineErrorState from '../../../components/states/InlineErrorState';
import PageCard from '../../../components/page-card';
import { useHelpArticles, HelpArticleDto } from '../hooks/useHelpArticles';
import { useHelpCategories } from '../hooks/useHelpCategories';
import { useDeleteHelpArticle } from '../hooks/useDeleteHelpArticle';
import { translate } from '../../../i18n';

export default function HelpArticleList() {
  const navigate = useNavigate();
  const toast = useToast();
  const deleteDialog = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Filters
  const [langFilter, setLangFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data: articles, isLoading, isError } = useHelpArticles({
    lang: langFilter || undefined,
    categoryId: categoryFilter || undefined,
    status: statusFilter || undefined,
  });
  /**
   * The articles read above has taken `isError` since this page was written;
   * the CATEGORIES read beside it had not, and its failure was invisible in two
   * places at once. `GET help/admin/categories` carries `@UseGuards(AdminAuthGuard)`
   * (`api/src/help-center/help-center.controller.ts`, read this run), so a stale
   * token or a non-admin operator is refused on top of the ordinary 5xx paths,
   * and `data === undefined` then made the filter offer nothing but "All
   * categories" while every row in the table claimed its article has no
   * category at all.
   *
   * The flag is bound off THIS call specifically — `isError` a line above
   * belongs to the articles read, and a reader (or a source-level gate whose
   * matcher over-reaches) can mistake one for the other. See R94-2.
   */
  const { data: categories, isError: isCategoriesError } = useHelpCategories();

  /**
   * A failed BACKGROUND refetch is not an unreadable list: React Query keeps the
   * last good `data` and raises `isError` beside it, so rendering the notice off
   * the flag alone would replace a correct, usable filter with an alert. The
   * defect is the absence of data, and that is what this asks.
   */
  const categoriesUnavailable = isCategoriesError && !categories;

  const deleteMutation = useDeleteHelpArticle(() => {
    deleteDialog.onClose();
    toast({ title: translate('help_center.article_deleted'), status: 'success', duration: 3000 });
  });

  const getCategoryName = (categoryId: string) => {
    const cat = categories?.find((c) => c.id === categoryId);
    // '—' is this table's way of saying "this article has no category". When the
    // category list could not be read we do not know that, and every row would
    // otherwise say it about an article that is perfectly well filed.
    if (categoriesUnavailable) return translate('common.unknown');
    return cat?.nameFr || cat?.nameEn || '—';
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <Box p={6}>
      <PageCard
        showBackButton
        title={translate('help_center.title')}
        rightHeader={
          <HStack>
            <Button
              leftIcon={<MdSettings />}
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/help-center/categories')}
            >
              {translate('help_center.manage_categories')}
            </Button>
            <Button
              leftIcon={<MdAdd />}
              colorScheme="brand"
              size="sm"
              onClick={() => navigate('/admin/help-center/new')}
            >
              {translate('help_center.new_article')}
            </Button>
          </HStack>
        }
      >
        {/* Filters */}
        <Flex gap={3} mb={4} wrap="wrap">
          {categoriesUnavailable ? (
            <Box maxW="260px">
              <InlineErrorState message={translate('common.unexpected_error')} />
            </Box>
          ) : (
            <Select
              size="sm"
              maxW="200px"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              placeholder="All categories"
            >
              {categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.nameFr}</option>
              ))}
            </Select>
          )}

          <HStack>
            <Tag
              size="md"
              variant={langFilter === '' ? 'solid' : 'outline'}
              colorScheme="gray"
              cursor="pointer"
              onClick={() => setLangFilter('')}
            >
              All
            </Tag>
            <Tag
              size="md"
              variant={langFilter === 'fr' ? 'solid' : 'outline'}
              colorScheme="brand"
              cursor="pointer"
              onClick={() => setLangFilter('fr')}
            >
              FR
            </Tag>
            <Tag
              size="md"
              variant={langFilter === 'en' ? 'solid' : 'outline'}
              colorScheme="brand"
              cursor="pointer"
              onClick={() => setLangFilter('en')}
            >
              EN
            </Tag>
          </HStack>

          <HStack>
            <Tag
              size="md"
              variant={statusFilter === '' ? 'solid' : 'outline'}
              colorScheme="gray"
              cursor="pointer"
              onClick={() => setStatusFilter('')}
            >
              All
            </Tag>
            <Tag
              size="md"
              variant={statusFilter === 'published' ? 'solid' : 'outline'}
              colorScheme="green"
              cursor="pointer"
              onClick={() => setStatusFilter('published')}
            >
              {translate('help_center.published')}
            </Tag>
            <Tag
              size="md"
              variant={statusFilter === 'draft' ? 'solid' : 'outline'}
              colorScheme="orange"
              cursor="pointer"
              onClick={() => setStatusFilter('draft')}
            >
              {translate('help_center.draft')}
            </Tag>
          </HStack>
        </Flex>

        {/* Table */}
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>{translate('help_center.article_title')}</Th>
              <Th>{translate('help_center.article_category')}</Th>
              <Th>{translate('help_center.article_language')}</Th>
              <Th>{translate('help_center.article_status')}</Th>
              <Th>Updated</Th>
              <Th width="60px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && (
              <Tr><Td colSpan={6}><Text color="gray.500">Loading...</Text></Td></Tr>
            )}
            {isError && (
              <Tr><Td colSpan={6}><Text color="red.500">Failed to load articles. Please try again.</Text></Td></Tr>
            )}
            {articles?.map((article) => (
              <Tr
                key={article.id}
                _hover={{ bg: 'gray.50' }}
                cursor="pointer"
                onClick={() => navigate(`/admin/help-center/${article.id}/edit`)}
              >
                <Td fontWeight="medium">{article.title}</Td>
                <Td>{getCategoryName(article.categoryId)}</Td>
                <Td>
                  <Badge colorScheme={article.language === 'fr' ? 'blue' : 'purple'}>
                    {article.language.toUpperCase()}
                  </Badge>
                </Td>
                <Td>
                  <Badge colorScheme={article.status === 'published' ? 'green' : 'orange'}>
                    {article.status === 'published' ? translate('help_center.published') : translate('help_center.draft')}
                  </Badge>
                </Td>
                <Td color="gray.500" fontSize="sm">{formatDate(article.updatedAt)}</Td>
                <Td onClick={(e) => e.stopPropagation()}>
                  <Menu>
                    <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" aria-label="Actions" />
                    <MenuList>
                      <MenuItem
                        icon={<MdEdit />}
                        onClick={() => navigate(`/admin/help-center/${article.id}/edit`)}
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        icon={<MdDelete />}
                        color="red.500"
                        onClick={() => {
                          setDeleteId(article.id);
                          deleteDialog.onOpen();
                        }}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
            {!isLoading && articles?.length === 0 && (
              <Tr>
                <Td colSpan={6}>
                  <Text color="gray.500" textAlign="center" py={8}>
                    No articles yet. Create your first article to get started.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </PageCard>

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{translate('help_center.delete_confirm')}</AlertDialogHeader>
            <AlertDialogBody>This action cannot be undone.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>Cancel</Button>
              <Button
                colorScheme="red"
                onClick={() => deleteId && deleteMutation.mutate(deleteId)}
                isLoading={deleteMutation.isLoading}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
